import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";

import App from "../App.js";

import Error from "../Components/Partials/Error";
import ContactUsPageSkeleton from "../Components/Partials/SkeletonUI/ContactUsSkeleton";
import LandingPageSkeleton from "../Components/Partials/SkeletonUI/LandingPageSkeleton";
import LoginPageSkeleton from "../Components/Partials/SkeletonUI/LoginPageSkeleton/index.js";
import OfferSummaryPageSkeleton from "../Components/Partials/SkeletonUI/OrderSummarypageSkeleton";
import RewardDetailSkeleton from "../Components/Partials/SkeletonUI/RewardDetailSkeleton";
import RewardListingSkeleton from "../Components/Partials/SkeletonUI/RewardListingSkeleton";

import { getEditNameStatus } from "../Components/Pages/Login/LoginFunction.js";

import Spinner from '../Components/Partials/Loading/Spinner.js';

import PrivateRoute from "./PrivateRoute.js";

import { getPreferredCardSelection, getSubdomainInfo, isMobSDKMode, MainDomainRedirect } from "../Utils/CommonFunctions.js";
import { SELECTEDCARD, SUBID, USERDATA } from "../Utils/Constants/Login.js";

import { useAuth } from "../hoc/AuthContext.js";

import { userDetail } from "../Services/APIs/RewardListing.js";

import ErrorBoundary from "../hoc/ErrorBoundary.js";

import {
  ACTIVE_SUBSCRIPTION_DETAILPAGE, AUTH, BRAND_BUNDLE_PAGE, BRAND_BUNDLE_SUMMARY_PAGE, COMMERCE_DETAIL, COMMERCE_MODULE, CONTACT_US_PAGE, COOKIE_POLICY_PAGE, DISCLAIMER_PAGE,
  EXPERIENCE_DETAIL, EXPERIENCE_MODULE, FAQ_PAGE, HOMEPAGE, INACTIVE_SUBSCRIPTION_DETAILPAGE, POLICY_PAGE, POLICY_PRIVACY_PAGE,
  PRECIOUS_METALS, PRECIOUS_METALS_BUY, PRECIOUS_METALS_BUY_CHECKOUT, PRECIOUS_METALS_BUY_PENDING, PRECIOUS_METALS_BUY_SUCCESS,
  PRECIOUS_METALS_MANAGE_HOLDINGS, PRECIOUS_METALS_PHYSICAL_DELIVERY, PRECIOUS_METALS_PHYSICAL_DELIVERY_CHECKOUT,
  PRECIOUS_METALS_PHYSICAL_DELIVERY_PENDING, PRECIOUS_METALS_PHYSICAL_DELIVERY_SUCCESS, PRECIOUS_METALS_PORTFOLIO,
  PRECIOUS_METALS_SELL, PRECIOUS_METALS_SELL_CHECKOUT, PRECIOUS_METALS_SELL_PENDING, PRECIOUS_METALS_SELL_SUCCESS,
  PUBLIC_LISTING_PAGE, PUBLIC_REWARD_DETAIL_PAGE, PUBLIC_REWARD_LISTING, REWARD_DETAIL_CASHBACK_PAGE, REWARD_DETAIL_DISCOUNTOFFER_PAGE,
  REWARD_DETAIL_GIFTCARD_PAGE, REWARD_DETAIL_MEMBERSHIP_PAGE, REWARD_DETAIL_SUBSCRIPTION_OFFER_PAGE, REWARD_LISTING,
  REWARD_SUMMARY_PAGE, SUBSCRIPTION_LISTING, TERMS_PAGE, TRAVEL_BUS_MODULE, TRAVEL_FLIGHT_CONFIRMATION, TRAVEL_FLIGHT_MODULE,
  TRAVEL_HOTEL_MODULE, TRAVEL_MODULE, TRAVEL_TRAIN_MODULE, USER_PROFILE, USER_PROFILE_LOYALTY_PAGE, USER_PROFILE_PRECIOUS_METAL_PAGE,
  USER_PROFILE_PRODUCT_MARKETPLACE_PAGE, USER_PROFILE_REWARD_BUNDLE_PAGE, USER_PROFILE_REWARD_CASHBACK_PAGE, USER_PROFILE_REWARD_DISCOUNT_PAGE,
  USER_PROFILE_REWARD_GIFTCARD_PAGE, USER_PROFILE_REWARD_SUBSCRIPTION_PAGE, USER_PROFILE_SETTING_PAGE, USER_PROFILE_SUBSCRIPTION_PAGE,
  USER_PROFILE_TRAVEL_BUS_PAGE, USER_PROFILE_TRAVEL_EXPERIENCES_PAGE, USER_PROFILE_TRAVEL_FLIGHT_PAGE, USER_PROFILE_TRAVEL_HOTEL_PAGE,
  USER_PROFILE_TRAVEL_TRAIN_PAGE, USER_PROFILE_TRAVELLER_INFO_PAGE, USER_PROFILE_UTILITIES_PAGE, VIP_DETAIL, VIP_MODULE
} from "./RoutesContant.js";

// Import the precious metals components
const BuyPendingPage = lazy(() => import("../Components/Pages/PreciousMetals/BuyMetal/BuyPendingPage"));
const BuySuccessPage = lazy(() => import("../Components/Pages/PreciousMetals/BuyMetal/BuySuccessPage"));
const BuyPage = lazy(() => import("../Components/Pages/PreciousMetals/BuyMetal"));
const CashCheckoutFlow = lazy(() => import("../Components/Pages/PreciousMetals/CommonComponents/CheckoutAddress"));
const PreciousMetals = lazy(() => import("../Components/Pages/PreciousMetals"));
const ManageHolding = lazy(() => import("../Components/Pages/PreciousMetals/ManageHolding"));
const MetalPhysicalDelivery = lazy(() => import("../Components/Pages/PreciousMetals/ManageHolding/MetalPhysicalDelivery"));
const PhysicalDeliveryCheckout = lazy(() => import("../Components/Pages/PreciousMetals/ManageHolding/MetalPhysicalDelivery/PhysicalDeliveryCheckout"));
const PhysicalDeliveryPendingPage = lazy(() => import("../Components/Pages/PreciousMetals/ManageHolding/MetalPhysicalDelivery/PhysicalDeliveryPending"));
const PhysicalDeliverySuccessPage = lazy(() => import("../Components/Pages/PreciousMetals/ManageHolding/MetalPhysicalDelivery/PhysicalDeliverySuccessPage"));
const SellMetal = lazy(() => import("../Components/Pages/PreciousMetals/ManageHolding/SellMetal"));
const SellPendingPage = lazy(() => import("../Components/Pages/PreciousMetals/ManageHolding/SellMetal/SellPendingPage"));
const SellSuccessPage = lazy(() => import("../Components/Pages/PreciousMetals/ManageHolding/SellMetal/SellSuccessPage"));
const Portfolio = lazy(() => import("../Components/Pages/PreciousMetals/Portfolio"));

// Lazy load the components

// main homepage and landing routing
const LandingPage = lazy(() => import("../Components/Pages/LandingPage/index.js"));
const HomePage = lazy(() => import("../Components/Pages/HomePage/index.js"));

// authentication routing
const LoginPage = lazy(() => import("../Components/Pages/Login/LoginPage.js"));

// Rewards module routing
const RewardPage = lazy(() => import("../Components/Pages/Reward/index.js"));
const BrandBundlePage = lazy(() => import("../Components/Pages/Reward/BrandBundle/index.js"));
const BrandBundleSummaryPage = lazy(() => import("../Components/Pages/Reward/BrandBundle/BrandBundleSummary.js"));
const RewardDetailMembershipPage = lazy(() => import("../Components/Pages/Reward/RewardDetailPage/AccessMembershipRewardDetailPage.js"));
const OfferSummaryPage = lazy(() => import("../Components/Pages/Reward/OfferSummary"));
const RewardDetailPage = lazy(() => import("../Components/Pages/Reward/RewardDetailPage/index.js"));

// User profile routing
const UserProfilePage = lazy(() => import("../Components/Pages/UserProfile/index.js"));

// profile page routing
const UserProfile = lazy(() => import("../Components/Pages/UserProfile/Components/MyAccount/Profile"));
const UserLoyalty = lazy(() => import("../Components/Pages/UserProfile/Components/MyAccount/Loyalty"));

// user profile reward routing
const UserProfileRewardDiscountPage = lazy(() => import("../Components/Pages/UserProfile/Components/Reward"));
const UserProfileRewardCashbackPage = lazy(() => import("../Components/Pages/UserProfile/Components/Reward"));
const UserProfileRewardGiftCardPage = lazy(() => import("../Components/Pages/UserProfile/Components/Reward"));
const UserProfileRewardSubscriptionPage = lazy(() => import("../Components/Pages/UserProfile/Components/Reward"));
const UserProfileRewardBundlePage = lazy(() => import("../Components/Pages/UserProfile/Components/Reward/Bundle.js"));

// user profile travel routing
const UserProfileTravelFlightPage = lazy(() => import("../Components/Pages/UserProfile/Components/Travel/Flight.js"));
const UserProfileTravelHotelPage = lazy(() => import("../Components/Pages/UserProfile/Components/Travel/Hotel.js"));
const UserProfileTravelTrainPage = lazy(() => import("../Components/Pages/UserProfile/Components/Travel/Train.js"));
const UserProfileTravelBusPage = lazy(() => import("../Components/Pages/UserProfile/Components/Travel/Bus.js"));
const UserProfileTravellerInfoPage = lazy(() => import("../Components/Pages/UserProfile/Components/Travel/TravellerInfo.js"));
const UserProfileTravelExperiencesPage = lazy(() => import("../Components/Pages/UserProfile/Components/Travel/Experience.js"));

// user profile utilities routing
const UserProfileUtilities = lazy(() => import("../Components/Pages/UserProfile/Components/Utilities"));

// user profile subscription routing
const UserProfileSubscription = lazy(() => import("../Components/Pages/UserProfile/Components/Subscription"));

// user profile precious metal routing
const UserProfilePreciousMetal = lazy(() => import("../Components/Pages/UserProfile/Components/PreciousMetal"));

// user profile product marketplace routing
const UserProfileProductMarketPlace = lazy(() => import("../Components/Pages/UserProfile/Components/ProductMarketPlace"));

// user setting routing
const Setting = lazy(() => import("../Components/Pages/UserProfile/Components/Setting"));

// contact and privacy policies pages
const ContactUsPage = lazy(() => import("../Components/Pages/ContactUsPage"));
const PolicyPage = lazy(() => import("../Components/Pages/PrivacyPolicyPage"));
const PrivacyPolicy = lazy(() => import("../Components/Pages/PrivacyPolicyPage/PrivacyPolicy.js"));
const Faq = lazy(() => import("../Components/Pages/PrivacyPolicyPage/Faq.js"));
const Disclaimer = lazy(() => import("../Components/Pages/PrivacyPolicyPage/Disclaimer.js"));
const Cookie = lazy(() => import("../Components/Pages/PrivacyPolicyPage/Cookie.js"));
const TermsOfUse = lazy(() => import("../Components/Pages/PrivacyPolicyPage/TermOfUse.js"));

// subscription routing 
const Subscription = lazy(() => import("../Components/Pages/Subscription/index.js"));
const ActiveSubscriptionDetail = lazy(() => import("../Components/Pages/Subscription/SubscriptionDetail.js"));
const InactiveSubscriptionDetail = lazy(() => import("../Components/Pages/Subscription/SubscriptionDetail.js"));

// Travel routing
const TravelPage = lazy(() => import("../Components/Pages/TravelModule/index.js"));
const FlightConfirmation = lazy(() => import("../Components/Pages/TravelModule/Flight/FlightConfirmation"));
// Commerce routing
const CommercePage = lazy(() => import("../Components/Pages/Commerce/index.js"));
const CommerceDetailPage = lazy(() => import("../Components/Pages/Commerce/CommerceDetail.js"));

// experience routing
const ExperiencePage = lazy(() => import("../Components/Pages/Experience/index.js"));
const ExperienceDetailPage = lazy(() => import("../Components/Pages/Experience/ExperienceDetail.js"));

// VIP routing
const VIPPage = lazy(() => import("../Components/Pages/VIP/index.js"));
const VIPDetailPage = lazy(() => import("../Components/Pages/VIP/VIPDetail.js"));

// Subdomain info
const { isSubdomain, subdomainName } = getSubdomainInfo();
const isMainDomain = !isSubdomain;
const windowURLSearchParam = window?.location?.search;
const windowLocationUrl = window?.location?.href;
const urlParams = new URLSearchParams(windowURLSearchParam);
const keyParam = urlParams.get('key');
const tokenParam = urlParams.get('token');
const cardIdParam = urlParams.get('card_id');
const uid = urlParams.get('UID');
const isMobileSDKMode = isMobSDKMode(windowURLSearchParam);

const AppWithState = () => {
  const {
    setWindowLocationUrl, setShowSignUp, isAuthenticated, setIsAuthenticated, setShowLogin, headerData, login, updateCreditCard, setSubVarientIdGlobal, setSelectedCardData, setUserData,
  } = useAuth();
  useEffect(() => {
    const hydrateUserFromToken = async () => {
      try {
        const result = await userDetail();
        if (result?.status === 200) {
          const editedUserData = getEditNameStatus(result?.data?.user);
          setUserData(editedUserData);
          localStorage.setItem(USERDATA, JSON.stringify(editedUserData));

          const cards = result?.data?.cards || [];
          updateCreditCard(cards);
          const savedCard = localStorage.getItem(SELECTEDCARD);
          const parsedSavedCard = savedCard ? JSON.parse(savedCard) : null;
          const { selectedCard, selectedCardId } = getPreferredCardSelection(cards, parsedSavedCard, windowURLSearchParam);

          if (selectedCardId !== null) {
            setSubVarientIdGlobal(selectedCardId);
            localStorage.setItem(SUBID, JSON.stringify(selectedCardId));
          }

          if (selectedCard && Object.keys(selectedCard).length > 0) {
            setSelectedCardData(selectedCard);
            localStorage.setItem(SELECTEDCARD, JSON.stringify(selectedCard));
          }
        }
      } catch (error) {
        console.error("Failed to hydrate user from token:", error);
      }
    };

    const removeConsumedQueryParams = () => {
      if (!tokenParam && !cardIdParam && !(isMobileSDKMode && !tokenParam)) {
        return;
      }

      const updatedUrl = new URL(window.location.href);
      updatedUrl.searchParams.delete('card_id');

      if (tokenParam) {
        updatedUrl.searchParams.delete('token');
      }

      if (isMobileSDKMode) {
        updatedUrl.searchParams.delete('mod');
      }

      window.history.replaceState({}, document.title, updatedUrl.toString());
    };

    if (isMobileSDKMode) {
      localStorage.clear();
      setIsAuthenticated(false);
      setShowLogin(false);
      setShowSignUp(false);
      if (!tokenParam) {
        removeConsumedQueryParams();
      }
    }

    if (!isMobileSDKMode && uid) {
      localStorage.setItem("UID", uid || '');
    }

    if (tokenParam) {
      login(tokenParam);
      hydrateUserFromToken();
      removeConsumedQueryParams();
    } else if (cardIdParam && isAuthenticated) {
      hydrateUserFromToken();
      removeConsumedQueryParams();
    } else if (cardIdParam) {
      removeConsumedQueryParams();
    }

    if (!isMobileSDKMode && keyParam) {
      // Store the card number in localStorage
      localStorage.setItem("card_number", keyParam);

      // Only show signup if user is not authenticated 
      if (!isAuthenticated) {
        setWindowLocationUrl(windowLocationUrl);
        headerData?.is_signin_present ? setShowLogin(true) : setShowSignUp(true);
      }
    }
    // Set windowLocationUrl when the component mounts
    setWindowLocationUrl(windowLocationUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <App />
  );
};

// Error boundary wrapper component
const WrapWithErrorBoundary = ({ children, skeleton }) => (
  <ErrorBoundary fallback={<Error />}>
    <Suspense fallback={skeleton || <Spinner />}>{children}</Suspense>
  </ErrorBoundary>
);

const OffersRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/${REWARD_LISTING}${location.search || ""}`} replace />;
};

// User Profile Routes Object
const userProfileRoutes = {
  path: "",
  element: <WrapWithErrorBoundary><UserProfilePage /></WrapWithErrorBoundary>,
  errorElement: <Error />,
  children: [
    // Profile routes
    {
      path: USER_PROFILE,
      element: <WrapWithErrorBoundary><UserProfile /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    // Loyalty routes
    {
      path: USER_PROFILE_LOYALTY_PAGE,
      element: <WrapWithErrorBoundary><UserLoyalty /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    // Redirect /userProfile to /userProfile/profile
    {
      path: "userProfile",
      element: <Navigate to={`/${USER_PROFILE}`} replace />,
    },
    // Reward routes
    {
      path: USER_PROFILE_REWARD_DISCOUNT_PAGE,
      element: <WrapWithErrorBoundary><UserProfileRewardDiscountPage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    {
      path: USER_PROFILE_REWARD_CASHBACK_PAGE,
      element: <WrapWithErrorBoundary><UserProfileRewardCashbackPage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    {
      path: USER_PROFILE_REWARD_GIFTCARD_PAGE,
      element: <WrapWithErrorBoundary><UserProfileRewardGiftCardPage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    {
      path: USER_PROFILE_REWARD_SUBSCRIPTION_PAGE,
      element: <WrapWithErrorBoundary><UserProfileRewardSubscriptionPage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    {
      path: USER_PROFILE_REWARD_BUNDLE_PAGE,
      element: <WrapWithErrorBoundary><UserProfileRewardBundlePage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    // Redirect /userProfile/reward to /userProfile/reward/discount
    {
      path: "userProfile/reward",
      element: <Navigate to={`/${USER_PROFILE_REWARD_DISCOUNT_PAGE}`} replace />,
    },
    // Travel routes
    {
      path: USER_PROFILE_TRAVEL_FLIGHT_PAGE,
      element: <WrapWithErrorBoundary><UserProfileTravelFlightPage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    {
      path: USER_PROFILE_TRAVEL_HOTEL_PAGE,
      element: <WrapWithErrorBoundary><UserProfileTravelHotelPage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    {
      path: USER_PROFILE_TRAVEL_TRAIN_PAGE,
      element: <WrapWithErrorBoundary><UserProfileTravelTrainPage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    {
      path: USER_PROFILE_TRAVEL_BUS_PAGE,
      element: <WrapWithErrorBoundary><UserProfileTravelBusPage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    {
      path: USER_PROFILE_TRAVELLER_INFO_PAGE,
      element: <WrapWithErrorBoundary><UserProfileTravellerInfoPage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    {
      path: USER_PROFILE_TRAVEL_EXPERIENCES_PAGE,
      element: <WrapWithErrorBoundary><UserProfileTravelExperiencesPage /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    // Redirect /userProfile/travel to /userProfile/travel/flight
    {
      path: "userProfile/travel",
      element: <Navigate to={`/${USER_PROFILE_TRAVEL_FLIGHT_PAGE}`} replace />,
    },
    // Subscription route
    {
      path: USER_PROFILE_SUBSCRIPTION_PAGE,
      element: <WrapWithErrorBoundary><UserProfileSubscription /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    // Precious Metal route
    {
      path: USER_PROFILE_PRECIOUS_METAL_PAGE,
      element: <WrapWithErrorBoundary><UserProfilePreciousMetal /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    // Utilities route
    {
      path: USER_PROFILE_UTILITIES_PAGE,
      element: <WrapWithErrorBoundary><UserProfileUtilities /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    // Product Marketplace route
    {
      path: USER_PROFILE_PRODUCT_MARKETPLACE_PAGE,
      element: <WrapWithErrorBoundary><UserProfileProductMarketPlace /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
    // Setting route
    {
      path: USER_PROFILE_SETTING_PAGE,
      element: <WrapWithErrorBoundary><Setting /></WrapWithErrorBoundary>,
      errorElement: <Error />,
    },
  ],
};

export const routes = createBrowserRouter([
  {
    path: '/',
    element: <>
      <MainDomainRedirect />
      <AppWithState />
    </>,
    errorElement: <Error />,
    children: [
      {
        path: HOMEPAGE,
        element:
          <WrapWithErrorBoundary skeleton={<LandingPageSkeleton />}>
            {isMainDomain ? <LandingPage /> : <HomePage subdomainName={subdomainName} />}
          </WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path : AUTH,
        element: <WrapWithErrorBoundary skeleton={<LoginPageSkeleton />}> <LoginPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: CONTACT_US_PAGE,
        element: <WrapWithErrorBoundary skeleton={<ContactUsPageSkeleton />}><ContactUsPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: PUBLIC_REWARD_LISTING,
        element: (
          <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
            <RewardPage windowURLSearchParam={windowURLSearchParam} isPublic={true} />
          </WrapWithErrorBoundary>
        ),
        errorElement: <Error />,
      },
      {
        path: PUBLIC_LISTING_PAGE,
        element: <OffersRedirect />,
        errorElement: <Error />,
      },
      {
        path: PUBLIC_REWARD_DETAIL_PAGE,
        element:
          <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}>
            <RewardDetailPage
              subdomainName={subdomainName}
              isSubdomain={isSubdomain}
              isPublic={true}
            />
          </WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: POLICY_PAGE,
        element: <WrapWithErrorBoundary><PolicyPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
        children: [
          {
            path: POLICY_PRIVACY_PAGE,
            element: <WrapWithErrorBoundary><PrivacyPolicy /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: TERMS_PAGE,
            element: <WrapWithErrorBoundary><TermsOfUse /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: FAQ_PAGE,
            element: <WrapWithErrorBoundary><Faq /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: DISCLAIMER_PAGE,
            element: <WrapWithErrorBoundary><Disclaimer /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: COOKIE_POLICY_PAGE,
            element: <WrapWithErrorBoundary><Cookie /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
        ],
      },
      // Travel Module routing
      {
        path: TRAVEL_MODULE,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><TravelPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: TRAVEL_FLIGHT_MODULE,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><TravelPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: TRAVEL_FLIGHT_CONFIRMATION,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><FlightConfirmation /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: TRAVEL_BUS_MODULE,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><TravelPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: TRAVEL_HOTEL_MODULE,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><TravelPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: TRAVEL_TRAIN_MODULE,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><TravelPage /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path : PRECIOUS_METALS,
        element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><PreciousMetals /></WrapWithErrorBoundary>,
        errorElement: <Error />,
      },
      {
        path: "/",
        element: <PrivateRoute />,
        errorElement: <Error />,
        children: [
          //  Subscription Module Routing
          {
            path: SUBSCRIPTION_LISTING,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <Subscription windowURLSearchParam={windowURLSearchParam} />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: ACTIVE_SUBSCRIPTION_DETAILPAGE,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><ActiveSubscriptionDetail active={true} /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: INACTIVE_SUBSCRIPTION_DETAILPAGE,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><InactiveSubscriptionDetail active={false} /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },

          //  VIP Module routing
          {
            path: VIP_MODULE,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <VIPPage windowURLSearchParam={windowURLSearchParam} />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: VIP_DETAIL,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><VIPDetailPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },

          //  Experience Module routing
          {
            path: EXPERIENCE_MODULE,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <ExperiencePage windowURLSearchParam={windowURLSearchParam} />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: EXPERIENCE_DETAIL,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><ExperienceDetailPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },

          //  Commerce Module routing
          {
            path: COMMERCE_MODULE,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <CommercePage windowURLSearchParam={windowURLSearchParam} />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: COMMERCE_DETAIL,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><CommerceDetailPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },

          //  Rewards Module routing
          {
            path: REWARD_LISTING,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <RewardPage windowURLSearchParam={windowURLSearchParam} isPublic={false} />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: `${BRAND_BUNDLE_PAGE}/:bundleId?`,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <BrandBundlePage />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: `${BRAND_BUNDLE_SUMMARY_PAGE}/:bundleId?`,
            element: (
              <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}>
                <BrandBundleSummaryPage />
              </WrapWithErrorBoundary>
            ),
            errorElement: <Error />,
          },
          {
            path: REWARD_DETAIL_GIFTCARD_PAGE,
            element: 
            <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}>
              {/* <RewardDetailGiftCardPage /> */}
              <RewardDetailPage isPublic={false} />
            </WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: REWARD_DETAIL_DISCOUNTOFFER_PAGE,
            element: 
            <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}>
              <RewardDetailPage isPublic={false} />
            </WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: REWARD_DETAIL_SUBSCRIPTION_OFFER_PAGE,
            element:
            <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}>
              <RewardDetailPage isPublic={false} />
            </WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: REWARD_DETAIL_MEMBERSHIP_PAGE,
            element: <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}><RewardDetailMembershipPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: REWARD_DETAIL_CASHBACK_PAGE,
            element:
            <WrapWithErrorBoundary skeleton={<RewardDetailSkeleton />}>
              <RewardDetailPage isPublic={false} />
            </WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: REWARD_SUMMARY_PAGE,
            element: <WrapWithErrorBoundary skeleton={<OfferSummaryPageSkeleton />}><OfferSummaryPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          // User Profile routing
          userProfileRoutes,
          // Precious Metals private routing
          // Portfolio
          {
            path: PRECIOUS_METALS_PORTFOLIO,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><Portfolio /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: PRECIOUS_METALS_MANAGE_HOLDINGS,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><ManageHolding /></WrapWithErrorBoundary>,
            errorElement: <Error />
          },
          // Buy Metal
          {
            path: PRECIOUS_METALS_BUY,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><BuyPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: PRECIOUS_METALS_BUY_CHECKOUT,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><CashCheckoutFlow transactionType="buy" /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: PRECIOUS_METALS_BUY_SUCCESS,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><BuySuccessPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: PRECIOUS_METALS_BUY_PENDING,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><BuyPendingPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          // Sell Metal
          {
            path: PRECIOUS_METALS_SELL,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><SellMetal /></WrapWithErrorBoundary>,
            errorElement: <Error />
          },
          {
            path: PRECIOUS_METALS_SELL_CHECKOUT,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><CashCheckoutFlow transactionType="sell" /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: PRECIOUS_METALS_SELL_SUCCESS,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><SellSuccessPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: PRECIOUS_METALS_SELL_PENDING,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><SellPendingPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          // physical delivery
          {
            path: PRECIOUS_METALS_PHYSICAL_DELIVERY,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><MetalPhysicalDelivery /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: PRECIOUS_METALS_PHYSICAL_DELIVERY_CHECKOUT,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><PhysicalDeliveryCheckout /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: PRECIOUS_METALS_PHYSICAL_DELIVERY_SUCCESS,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><PhysicalDeliverySuccessPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
          {
            path: PRECIOUS_METALS_PHYSICAL_DELIVERY_PENDING,
            element: <WrapWithErrorBoundary skeleton={<RewardListingSkeleton />}><PhysicalDeliveryPendingPage /></WrapWithErrorBoundary>,
            errorElement: <Error />,
          },
        ],
      },
    ],
  },
]);
